import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { flow } from 'lodash-es';
import { useSelector } from 'react-redux';
import Button from 'reactstrap/lib/Button';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/canvas/canvas';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PageModel } from 'client/data/models/page';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { CANVAS_CREATIVE_ID, CANVAS_ICONS } from 'site-modules/canvas/constants/canvas';
import { addCanvasLinkRef } from 'site-modules/canvas/utils/canvas-links';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { CanvasLlmClient } from 'site-modules/canvas/utils/canvas-llm-client';
import { CanvasLocationSync } from 'site-modules/shared/components/canvas/canvas-location-sync/canvas-location-sync';
import { DockedPanel } from 'site-modules/canvas/components/docked-panel/docked-panel';
import { McpAppsChatCanvas } from 'site-modules/canvas/components/chat-canvas/mcp-apps-chat-canvas';
import { elementScrollArrowStrategy } from 'site-modules/canvas/components/scroll-down-arrow/scroll-arrow-strategies';
import { ElementScrollChatMessages } from 'site-modules/canvas/components/chat-messages/element-scroll-chat-messages';
import { useWebShare } from 'site-modules/shared/hooks/use-web-share/use-web-share';
import { getShareData } from 'site-modules/canvas/utils/get-share-data';
import { useIframeLoad } from 'site-modules/canvas/hooks/use-iframe-load';
import { VerticalActionPanel } from 'site-modules/canvas/components/vertical-action-panel/vertical-action-panel';
import { StartScreenHeader } from 'site-modules/canvas/components/start-screen-header/start-screen-header';
import { SidePanelActionButton } from 'site-modules/canvas/components/side-panel-action-button/side-panel-action-button';
import { InsiderActionButton } from 'site-modules/canvas/components/insider-action-button/insider-action-button';
import { GradientStroke } from 'site-modules/canvas/components/gradient-stroke/gradient-stroke';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { isLinkWithSameOrigin } from 'site-modules/canvas/utils/is-same-origin';
import { connectedProfileAuth } from 'site-modules/shared/components/profile/profile-auth';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { profileScreenMethods } from 'site-modules/shared/components/profile/profile-screen-methods';
import { ApiMetrics, withMetrics } from 'client/utils/metrics-hoc';
import { CanvasInitializationOverlay } from 'site-modules/canvas/components/canvas-initialization-overlay/canvas-initialization-overlay';

import './canvas-desktop.scss';

const SPLIT_VIEW_CREATIVE_ID = 'slide-up-ui';
const CODRIVER_AUTH_ACTION = {
  creativeId: 'codriver',
  signInTrackingId: 'codriver_begin_sign_up',
  signOutTrackingId: 'codriver_sign_out',
};

function CanvasUI({
  location = null,
  authAction = CODRIVER_AUTH_ACTION,
  visitorId = null,
  visitorLocation = null,
  sessionEntryParams = null,
  sessionId,
  isAuthenticated = false,
  isAnonymousUser = false,
  createOrUpdateIdmData = null,
  toggleSignModal,
  signOut,
  apiMetrics = null,
  LlmClient = CanvasLlmClient,
}) {
  const [iframeSrc, setIframeSrc] = useState('');
  const [isChatInProgress, setIsChatInProgress] = useState(false);
  const [isChatInitializing, setIsChatInitializing] = useState(true);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const iframeRef = useRef();
  const chatCanvasRef = useRef();
  const messagesContainerRef = useRef();
  const messagesRef = useRef();
  const search658Recipe = useSelector(state =>
    ExperimentUtil.getForcedOrAssignedRecipeName({
      state,
      campaignName: 'search-658-mcp-rc',
      defaultVal: 'ctrl',
    })
  );
  const llmClient = useRef(
    new LlmClient({
      iframeRef,
      visitorId,
      sessionId,
      visitorLocation,
      sessionEntryParams,
      search658Recipe,
      createOrUpdateIdmData,
      apiMetrics,
    })
  );
  // eslint-disable-next-line react-hooks/refs -- strategy receives refs for deferred scroll handling
  const scrollArrowStrategy = useMemo(() => elementScrollArrowStrategy(messagesContainerRef, messagesRef), []);

  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);

  const { onLoad, isLoading } = useIframeLoad({ iframeRef, location, iframeSrc });

  useEffect(() => {
    llmClient.current.updateVisitorLocation(visitorLocation);
  }, [visitorLocation]);

  useEffect(() => {
    llmClient.current.updateAuthContext({ createOrUpdateIdmData, apiMetrics });
  }, [apiMetrics, createOrUpdateIdmData]);

  const onClearClick = useCallback(() => {
    if (!isChatInProgress) return;

    chatCanvasRef.current?.clearChat();
    setIframeSrc('');
  }, [isChatInProgress]);

  const shareData = useMemo(() => getShareData(), []);
  const { isSupported, share } = useWebShare(shareData);

  const actionButtons = [
    <SidePanelActionButton
      key="clear"
      icon={CANVAS_ICONS.EDIT_SQUARE_WHITE}
      label="Clear chat and start new"
      trackingId="select_autocomplete"
      trackingValue="new-chat"
      onClick={onClearClick}
      hoverCopy="New Chat"
    />,
    isSupported && isChatInProgress && (
      <SidePanelActionButton
        key="share"
        icon={CANVAS_ICONS.SHARE_WHITE}
        label="Share the AI assistant page"
        trackingId="share_chat"
        onClick={share}
        buttonClassNames="share-button pos-r"
        hoverCopy="Share"
      />
    ),
  ].filter(Boolean);

  const fireLinkTracking = useCallback((href, messageId) => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
        action_name: TrackingConstant.ACTION_VIEW_CONTENT,
        subaction_name: TrackingConstant.ACTION_VIEW_CONTENT,
        creative_id: CANVAS_CREATIVE_ID,
        value: href,
        ...(messageId ? { resp_uuid: messageId } : {}),
      },
    });

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
      event_data: {
        action_name: TrackingConstant.ACTION_WIDGET_VIEW,
        action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
        total_page_height: Math.max(document.body.scrollHeight, document.body.offsetHeight),
        creative_id: SPLIT_VIEW_CREATIVE_ID,
      },
    });
  }, []);

  const openSameOrigin = useCallback(
    ({ event, href }) => {
      event?.preventDefault?.();

      const linkToOpen = addCanvasLinkRef(href);

      if (linkToOpen === iframeSrc) {
        if (iframeRef.current) {
          iframeRef.current.src = iframeSrc;
        }
      } else {
        setIframeSrc(linkToOpen);
      }
    },
    [iframeSrc]
  );

  const onLinkClick = useCallback(
    (event, messageId) => {
      const href = event.currentTarget?.getAttribute('href');

      fireLinkTracking(href, messageId);

      const isSameOrigin = event.currentTarget?.getAttribute('target') !== '_blank';

      if (isSameOrigin) {
        openSameOrigin({ event, href });
      }
    },
    [fireLinkTracking, openSameOrigin]
  );

  const onMCPLinkClick = useCallback(
    ({ url }, messageId) => {
      fireLinkTracking(url, messageId);

      const isSameOrigin = isLinkWithSameOrigin(url);

      if (isSameOrigin) {
        openSameOrigin({ href: url });
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    [fireLinkTracking, openSameOrigin]
  );

  const onIframeClose = useCallback(() => {
    setIframeSrc('');
  }, [setIframeSrc]);

  const messagesRenderer = useCallback(rendererProps => {
    return (
      <ElementScrollChatMessages
        messagesContainerRef={messagesContainerRef}
        messagesRef={messagesRef}
        {...rendererProps}
      />
    );
  }, []);

  return (
    <div className="canvas-desktop pos-r overflow-hidden d-flex flex-column">
      <GradientStroke className="pos-a top-0 left-0 right-0" withAnimation={isWaitingForResponse} />
      <main className="main-container pos-r px-0 d-flex">
        <VerticalActionPanel
          bottomAction={
            <InsiderActionButton
              authAction={authAction}
              isAuthenticated={isAuthenticated}
              isAnonymousUser={isAnonymousUser}
              toggleSignModal={toggleSignModal}
              signOut={signOut}
            />
          }
        >
          {actionButtons}
        </VerticalActionPanel>
        <DockedPanel
          iframeRef={iframeRef}
          trackingParent={CANVAS_CREATIVE_ID}
          fullWidth={!iframeSrc}
          resizeContainerClassname="align-items-center"
        >
          {!isChatInProgress && <StartScreenHeader copyClasses="size-36 mb-2" />}
          <div
            className={classnames('chat-canvas-content d-flex flex-column w-100', {
              'flex-fill': isChatInProgress,
              'h-100': !isChatInProgress,
            })}
          >
            <McpAppsChatCanvas
              ref={chatCanvasRef}
              iframeRef={iframeRef}
              llmClient={llmClient}
              onLinkClick={onLinkClick}
              onMCPLinkClick={onMCPLinkClick}
              trackingParent={CANVAS_CREATIVE_ID}
              onChatProgressChange={setIsChatInProgress}
              onInitializationChange={setIsChatInitializing}
              onLoadingChange={setIsWaitingForResponse}
              isAuthenticated={isAuthenticated}
              scrollArrowStrategy={scrollArrowStrategy}
              MessagesRenderer={messagesRenderer}
              location={location}
              inputContainerClassName="chat-input-panel w-100 mx-auto"
            />
          </div>
        </DockedPanel>
        {isChatInitializing && (
          <CanvasInitializationOverlay
            className="canvas-desktop-initialization-overlay pos-a"
            contentClassName="canvas-desktop-initialization-content pos-a"
          />
        )}
        {!!iframeSrc && (
          <div
            className="content-container pos-r flex-fill h-100 overflow-hidden"
            data-tracking-parent={SPLIT_VIEW_CREATIVE_ID}
            data-no-widget-view
          >
            <div className="d-flex flex-column h-100">
              <div className="iframe-close-section d-flex justify-content-end align-items-center bg-primary">
                <div className="iframe-close-container h-100 w-100 d-flex justify-content-end align-items-center px-0_5 mx-auto">
                  <Button
                    aria-label="Close iframe"
                    onClick={onIframeClose}
                    className="iframe-close-btn p-0 m-0 border-0 bg-transparent text-white d-flex justify-content-center align-items-center bg-blue-40 rounded-circle"
                    data-tracking-id="tool_select"
                  >
                    <span className="icon-cross2 size-16" aria-hidden="true" />
                  </Button>
                </div>
              </div>
              <LoadingSpinner classes="flex-fill bg-white z-index-1" isLoading={isLoading}>
                <div className="d-flex flex-column w-100 h-100">
                  <iframe
                    ref={iframeRef}
                    className="edmunds-iframe w-100 flex-fill"
                    src={iframeSrc}
                    border={0}
                    onLoad={onLoad}
                    title="Edmunds"
                  />
                </div>
              </LoadingSpinner>
            </div>
          </div>
        )}
      </main>
      <CanvasLocationSync iframeRef={iframeRef} />
    </div>
  );
}

CanvasUI.propTypes = {
  authAction: PropTypes.shape({
    creativeId: PropTypes.string.isRequired,
    signInTrackingId: PropTypes.string.isRequired,
    signOutTrackingId: PropTypes.string.isRequired,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  visitorId: PropTypes.string,
  sessionId: PropTypes.string.isRequired,
  visitorLocation: VisitorEntities.Location,
  sessionEntryParams: PropTypes.shape({
    entryPage: PropTypes.string,
    entryUrl: PropTypes.string,
  }),
  isAuthenticated: PropTypes.bool,
  isAnonymousUser: PropTypes.bool,
  createOrUpdateIdmData: PropTypes.func,
  toggleSignModal: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired,
  apiMetrics: ApiMetrics,
  LlmClient: PropTypes.func,
};

export const Canvas = flow(
  component =>
    connectToModel(component, {
      location: bindToPath('location', PageModel),
      visitorId: bindToPath('id', VisitorModel),
      sessionId: bindToPath('session', VisitorModel),
      visitorLocation: bindToPath('location', VisitorModel),
      sessionEntryParams: bindToPath('sessionEntryParams', VisitorModel),
    }),
  withMetrics,
  insiderMethods,
  profileScreenMethods,
  connectedProfileAuth
)(CanvasUI);
